import { ReactNode } from 'react';
import {
  GroupOptionToggle,
  ProductGroupMultiselectFilter,
  SideFilters,
  ToggleGroup,
  ToggleSwitch,
} from '@retail/components';
import { HierarchyFilterContext, AssortmentView } from '@retail/my-assortment/types';
import {
  ItemIdentifiersFilterButton,
  MgSupplierItemSearchField,
  SelectDiscountGroups,
} from '@retail/products/components';
import { SelectSupplier } from '@retail/suppliers/components';
import {
  GroupOption,
  ProductGroupCount,
  ProductGroup,
  DiscountGroup,
} from '@retail/products/types';
import { AssortmentViewRadio } from '../AssortmentFilters';
import { ExcludeVatSwitch } from '../ExcludeVatSwitch';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { SupplierBase } from '@retail/suppliers/types';
import { baseAssortmentFilters, BaseAssortmentFilters } from '@retail/my-assortment/utils';
import { useFilters } from '@retail/products/hooks';
import { AssortmentTagSelect } from '../AssortmentItems';
import { useProductsTFunction } from '@retail/products/i18n';

interface Props {
  filterOptions?: HierarchyFilterContext;
  includeVat: boolean;
  showFilters: boolean;
  canReadCompetitorPrices: boolean;
  showFromMainSupplier?: boolean;
  toggleIncludeVat: () => void;
  onOpenItemIdentifiersFilter?: () => void;
  header?: ReactNode;
}

export function AssortmentPricesFilters({
  filterOptions,
  includeVat,
  showFilters,
  canReadCompetitorPrices,
  showFromMainSupplier,
  toggleIncludeVat,
  onOpenItemIdentifiersFilter,
  header,
}: Props) {
  const t = useMyAssortmentTFunction();
  const tProducts = useProductsTFunction();
  const { filters, updateFilter, setFilters } =
    useFilters<BaseAssortmentFilters>(baseAssortmentFilters);

  const {
    supplierNumber,
    productGroupNumbers,
    discountGroupCodes,
    subAssortmentIds,
    assortmentView,
    purchasePrice,
    activeItems,
    searchQuery,
    forApproval,
    inStock,
    fromMainSupplier,
  } = filters;

  const overGroup: ProductGroupCount[] =
    filterOptions?.productGroups?.map(
      (productGroupCount) => new ProductGroupCount(productGroupCount)
    ) ?? [];

  return (
    <SideFilters showFilters={showFilters} sx={{ py: 1 }}>
      {header}
      <AssortmentViewRadio
        selectedAssortmentView={assortmentView as AssortmentView}
        selectAssortmentView={(assortmentView: AssortmentView) =>
          setFilters({
            assortmentView: assortmentView === 'ASSORTMENT_FULL' ? undefined : assortmentView,
          })
        }
        canReadCompeititorPrices={canReadCompetitorPrices}
      />

      {assortmentView !== 'COMPETITOR_PRICES' && (
        <>
          <ExcludeVatSwitch
            toggleVat={includeVat}
            handleToggleVat={toggleIncludeVat}
            justifyContent="space-between"
          />
          <ToggleSwitch
            toggle={forApproval}
            setToggle={() => updateFilter('forApproval', forApproval)}
            justifyContent="space-between"
            text={t('myAssortment.filters.onlyForApprovalItems')}
          />
          {showFromMainSupplier && (
            <ToggleSwitch
              toggle={fromMainSupplier}
              setToggle={() => updateFilter('fromMainSupplier', fromMainSupplier)}
              justifyContent="space-between"
              text={tProducts('products.search.onlyProductOwners')}
            />
          )}
          <GroupOptionToggle
            label={t('myAssortment.columns.purchasePrice.price')}
            value={purchasePrice}
            labelDirection="horizontal"
            justifyContent="space-between"
            fullWidth={false}
            onChange={(option: GroupOption) => updateFilter('purchasePrice', option)}
          />
          <ToggleGroup
            variant="pill"
            label={t('myAssortment.filters.activeItems.title')}
            value={activeItems}
            justifyContent="space-between"
            size="small"
            description={t('myAssortment.filters.activeItems.description', {
              status1: t('myAssortment.discontinuationStatus.removed'),
              status2: t('myAssortment.discontinuationStatus.soldOut'),
            })}
            options={[
              { label: t('myAssortment.filter.all'), value: 'UNSPECIFIED' },
              { label: t('myAssortment.filter.active'), value: 'YES' },
              { label: t('myAssortment.filter.inactive'), value: 'NO' },
            ]}
            onChange={(option) => updateFilter('activeItems', option)}
          />

          <ToggleGroup
            variant="pill"
            label={t('myAssortment.stockStatus.status')}
            value={inStock}
            justifyContent="space-between"
            size="small"
            options={[
              { label: t('myAssortment.filter.all'), value: 'UNSPECIFIED' },
              { label: t('myAssortment.stockStatus.inStock'), value: 'YES' },
              { label: t('myAssortment.stockStatus.order'), value: 'NO' },
            ]}
            onChange={(option) => updateFilter('inStock', option)}
          />
        </>
      )}

      {filterOptions?.assortmentTags && (
        <AssortmentTagSelect
          includeUnselectedLevel={false}
          assortmentTags={filterOptions.assortmentTags}
          selectedTags={filterOptions.assortmentTags.filter((tag) =>
            subAssortmentIds.includes(tag.id.toString())
          )}
          selectTagById={(tagId: number) => updateFilter('subAssortmentIds', tagId.toString())}
          resetTags={() => null}
        />
      )}

      <SelectSupplier
        suppliers={filterOptions?.suppliers ?? []}
        selectedSupplier={supplierNumber ? Number(supplierNumber) : null}
        getSupplierId={(supplier: SupplierBase) => supplier.supplierNumber}
        getSupplierName={(supplier: SupplierBase) => supplier.name}
        selectSupplier={(supplierNumber: number | null) => {
          updateFilter('supplierNumber', supplierNumber ? supplierNumber.toString() : null);
        }}
      />
      <SelectDiscountGroups
        discountGroups={filterOptions?.discountGroupsForSupplier ?? []}
        selectedDiscountGroups={discountGroupCodes}
        getDiscountGroupKey={({ code }: DiscountGroup) => code}
        disabled={!supplierNumber}
        disabledReason={tProducts('products.search.discountGroup.selectSupplierFirst')}
        selectDiscountGroups={(groupCodes: string[]) =>
          setFilters({ discountGroupCodes: groupCodes })
        }
      />
      <MgSupplierItemSearchField
        size="small"
        width="100%"
        searchTerm={searchQuery ?? undefined}
        isDisabled={false}
        onSubmitSearch={(query: string) => setFilters({ searchQuery: query })}
      />
      {onOpenItemIdentifiersFilter && (
        <ItemIdentifiersFilterButton onClick={onOpenItemIdentifiersFilter} />
      )}
      <ProductGroupMultiselectFilter
        productGroups={overGroup}
        selectedProductGroups={productGroupNumbers ?? []}
        selectProductGroup={(groupNumber: string) =>
          updateFilter('productGroupNumbers', groupNumber)
        }
        getCount={(productGroup: ProductGroup) => productGroup.count}
      />
    </SideFilters>
  );
}
